import { useState, useEffect } from 'react';
import { Megaphone, Clock, AlertTriangle, Info, Bell } from 'lucide-react';
import { useBackgrounds } from '../context/BackgroundContext';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultAnnouncements = [
    {
        id: '1',
        title: 'Operation Crimson Fang Commences',
        content: 'All pilots report to hangar bay at 0600 UTC. Full combat loadout required. Escort mission to Pyro Gate Alpha.',
        author: 'Admiral Wolfheart',
        priority: 'urgent',
        created_at: new Date().toISOString()
    },
    {
        id: '2',
        title: 'New Squadron Formation: Ghost Recon',
        content: 'Proud to announce the formation of our new reconnaissance squadron. Lieutenant Shadow will lead intel operations.',
        author: 'Captain Nova',
        priority: 'high',
        created_at: new Date(Date.now() - 86400000).toISOString()
    },
    {
        id: '3',
        title: 'Fleet Maintenance Schedule',
        content: 'All vessels will undergo routine maintenance this weekend. Please ensure your ships are docked by Friday 2200 UTC.',
        author: 'Commander Steele',
        priority: 'normal',
        created_at: new Date(Date.now() - 172800000).toISOString()
    },
    {
        id: '4',
        title: 'Training Sessions - Combat Maneuvers',
        content: 'Weekly combat training sessions will resume. All ranks welcome. Focus on formation flying and target prioritization.',
        author: 'Lieutenant Vex',
        priority: 'low',
        created_at: new Date(Date.now() - 259200000).toISOString()
    }
];

const priorityConfig = {
    urgent: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    high: { icon: Bell, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    normal: { icon: Megaphone, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    low: { icon: Info, color: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' }
};

export const Announcements = () => {
    const { getBackground } = useBackgrounds();
    const [announcements, setAnnouncements] = useState(defaultAnnouncements);
    const [filter, setFilter] = useState('all');
    
    const announcementsBg = getBackground('announcements');

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const response = await axios.get(`${API}/announcements`);
            if (response.data.length > 0) {
                setAnnouncements(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
        }
    };

    const filteredAnnouncements = filter === 'all' 
        ? announcements 
        : announcements.filter(a => a.priority === filter);

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="announcements-page">
            {/* Hero */}
            <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
                {announcementsBg && (
                    <>
                        <div 
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${announcementsBg})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                    </>
                )}
                <div className="relative z-10 text-center px-4">
                    <span className="font-mono text-xs text-red-500 tracking-[0.3em]">COMMS CENTER</span>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase mt-2">
                        Announcements
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-4">
                        Fleet-Wide Communications • Priority Transmissions
                    </p>
                </div>
            </section>

            {/* Filter */}
            <section className="px-4 py-6 border-b border-white/10">
                <div className="max-w-4xl mx-auto">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {['all', 'urgent', 'high', 'normal', 'low'].map((priority) => (
                            <button
                                key={priority}
                                onClick={() => setFilter(priority)}
                                className={`px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                                    filter === priority
                                        ? 'bg-red-500 text-white'
                                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                }`}
                                data-testid={`filter-${priority}`}
                            >
                                {priority}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Announcements List */}
            <section className="px-4 py-12">
                <div className="max-w-4xl mx-auto space-y-6">
                    {filteredAnnouncements.map((announcement) => {
                        const config = priorityConfig[announcement.priority] || priorityConfig.normal;
                        const Icon = config.icon;
                        const date = new Date(announcement.created_at);
                        
                        return (
                            <div 
                                key={announcement.id} 
                                className={`tactical-panel p-6 ${config.border} border-l-4`}
                                data-testid={`announcement-${announcement.id}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`p-2 ${config.bg} ${config.border} border`}>
                                        <Icon className={`w-5 h-5 ${config.color}`} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-4 mb-2">
                                            <h3 className="font-heading font-bold text-lg text-white">
                                                {announcement.title}
                                            </h3>
                                            <span className={`badge ${
                                                announcement.priority === 'urgent' ? 'badge-urgent' :
                                                announcement.priority === 'high' ? 'badge-pending' :
                                                'badge-active'
                                            }`}>
                                                {announcement.priority}
                                            </span>
                                        </div>
                                        <p className="font-body text-gray-400 mb-4">
                                            {announcement.content}
                                        </p>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <span className="font-mono">{announcement.author}</span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {format(date, 'yyyy-MM-dd HH:mm')} UTC
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {filteredAnnouncements.length === 0 && (
                        <div className="text-center py-12">
                            <Megaphone className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                            <p className="font-mono text-gray-500">No announcements found</p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};
